import React from 'react';
import Session from '../../models/Session';
import SessionItem from '../../models/SessionItem';
import { formatInputType } from '../../utils/Utils';

interface PrintWindowProps {
    session: Session;
    isBmw: boolean;
    /** Items in the order/filter currently shown in the UI. */
    filteredItems: SessionItem[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function esc(str?: string | null): string {
    if (!str) return '—';
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * Format a date value for display.
 * Accepts ISO strings, timestamps, or already-formatted strings.
 * Falls back to the raw value when the date cannot be parsed.
 */
function formatDate(dateStr?: string | null): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr; // already formatted by the server — display as-is
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    }).format(d);
}

function computeStatus(item: SessionItem): string {
    const { quantity, shipQtd } = item;
    if (shipQtd === 0 && quantity === 0) return 'NA';
    if (shipQtd === 0 && quantity > 0)  return 'UNEXPECTED';
    if (quantity === shipQtd)           return 'OK';
    if (quantity < shipQtd)             return 'NO';
    return 'OVER';
}

function statusStyle(status: string): string {
    switch (status) {
        case 'OK':         return 'background:#dcfce7;color:#166534;';
        case 'NO':         return 'background:#fee2e2;color:#991b1b;';
        case 'OVER':       return 'background:#fef9c3;color:#854d0e;';
        case 'UNEXPECTED': return 'background:#fed7aa;color:#9a3412;';
        default:           return 'background:#f3f4f6;color:#6b7280;';
    }
}

const TD = 'border:1px solid #d1d5db;padding:3px 6px;font-size:9px;vertical-align:top;';
const TH = 'background:#1e3a8a;color:#fff;padding:4px 6px;font-size:9px;text-align:left;border:1px solid #1e40af;';

function metaTd(label: string, value: string): string {
    return `<td style="padding:4px 12px 4px 0;vertical-align:top;width:25%;">
        <div style="font-size:8px;color:#6b7280;text-transform:uppercase;font-weight:bold;letter-spacing:.04em;">${label}</div>
        <div style="font-size:10px;color:#111827;">${value}</div>
    </td>`;
}

// ── HTML builder ──────────────────────────────────────────────────────────────

function buildPrintHTML(session: Session, isBmw: boolean, filteredItems: SessionItem[]): string {
    const items = filteredItems;

    const stateLabel      = session.posted ? 'Posted' : session.closed ? 'Closed' : 'Open';
    const stateBadgeStyle = session.posted
        ? 'background:#f1f5f9;color:#475569;'
        : session.closed
        ? 'background:#dcfce7;color:#166534;'
        : 'background:#fef9c3;color:#854d0e;';

    const deliveryNotes = session.deliveryNotes?.length
        ? esc(session.deliveryNotes.join(', '))
        : '—';

    // WIP description row — BMW only
    const descriptionRow = isBmw && session.description
        ? `<p style="font-size:10px;color:#1d4ed8;margin:2px 0 6px 0;">${esc(session.description)}</p>`
        : '';

    // State badge cell
    const stateTd = `<td style="padding:4px 0;vertical-align:top;width:25%;">
        <div style="font-size:8px;color:#6b7280;text-transform:uppercase;font-weight:bold;letter-spacing:.04em;">State</div>
        <div><span style="display:inline-block;padding:1px 8px;border-radius:10px;font-size:9px;font-weight:bold;${stateBadgeStyle}">${stateLabel}</span></div>
    </td>`;

    // Closed-session row
    const closedRow = session.closed ? `
    <tr>
        ${metaTd('Closed Date', formatDate(session.closedDate))}
        ${metaTd('Closed By',   esc(session.closedByName))}
        <td colspan="2"></td>
    </tr>` : '';

    // Posted-session row
    const postedRow = session.posted ? `
    <tr>
        ${metaTd('Posted Date', formatDate(session.postedDate))}
        ${metaTd('Posted By',   esc(session.postedByName))}
        <td colspan="2"></td>
    </tr>` : '';

    // Wip No. column
    const wipTh = isBmw ? `<th style="${TH}">Wip No.</th>` : '';

    // Item rows
    const itemRows = items.map((item, idx) => {
        const status = computeStatus(item);
        const wipTd  = isBmw ? `<td style="${TD}">${esc(item.wipNo)}</td>` : '';
        const rowBg  = idx % 2 === 1 ? 'background:#f9fafb;' : '';
        return `<tr style="${rowBg}">
            <td style="${TD}text-align:center;">${idx + 1}</td>
            <td style="${TD}">${esc(item.partNumber)}</td>
            <td style="${TD}">${esc(item.description)}</td>
            ${wipTd}
            <td style="${TD}text-align:center;">${item.shipQtd}</td>
            <td style="${TD}text-align:center;">${item.quantity}</td>
            <td style="${TD}text-align:center;font-weight:bold;${statusStyle(status)}">${status}</td>
            <td style="${TD}">${esc(item.deliveryNotes?.join(', '))}</td>
            <td style="${TD}">${esc(item.orderNumber?.join(', '))}</td>
            <td style="${TD}text-align:center;">${esc(formatInputType(item.inputType))}</td>
            <td style="${TD}">${item.lastUpdate ? formatDate(item.lastUpdate) : ''}</td>
        </tr>`;
    }).join('');

    return `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8"/>
    <title>${esc(session.title)}</title>
    <style>
        @page { size: landscape; margin: 12mm; }
        @media print {
            body { margin: 0; }
            * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        body  { font-family: Arial, Helvetica, sans-serif; font-size: 10px; color: #1a1a1a; margin: 0; }
        table { width: 100%; border-collapse: collapse; }
    </style>
</head>
<body>

<!-- ── Header ───────────────────────────────────────────────────────────── -->
<div style="border-bottom:2px solid #2563eb;padding-bottom:8px;margin-bottom:10px;">

    <p style="font-size:16px;font-weight:bold;color:#1e3a8a;margin:0 0 2px 0;">${esc(session.title)}</p>
    ${descriptionRow}

    <!-- Metadata grid — real <table> with table-layout:fixed to enforce even columns -->
    <table style="margin-top:8px;table-layout:fixed;border:none;">
        <colgroup>
            <col style="width:25%;"/>
            <col style="width:25%;"/>
            <col style="width:25%;"/>
            <col style="width:25%;"/>
        </colgroup>
        <tbody>
            <tr>
                ${metaTd('Delivery Notes', deliveryNotes)}
                ${metaTd('Created Date',   formatDate(session.createdDate))}
                ${metaTd('Created By',     esc(session.createdByName))}
                ${stateTd}
            </tr>
            ${closedRow}
            ${postedRow}
            <tr>
                ${metaTd('Lines Total', String(items.length))}
                <td colspan="3"></td>
            </tr>
        </tbody>
    </table>

    <p style="font-size:8px;color:#9ca3af;margin:6px 0 0 0;">Generated: ${formatDate(new Date().toISOString())} UTC</p>
</div>

<!-- ── Items Table ───────────────────────────────────────────────────────── -->
<table style="margin-top:8px;">
    <thead>
        <tr>
            <th style="${TH}text-align:center;width:28px;">#</th>
            <th style="${TH}">Part Number</th>
            <th style="${TH}">Description</th>
            ${wipTh}
            <th style="${TH}text-align:center;">Ship Qty</th>
            <th style="${TH}text-align:center;">Scanned Qty</th>
            <th style="${TH}text-align:center;">Status</th>
            <th style="${TH}">Delivery Note(s)</th>
            <th style="${TH}">Order Number(s)</th>
            <th style="${TH}text-align:center;">Input Type</th>
            <th style="${TH}">Last Update</th>
        </tr>
    </thead>
    <tbody>
        ${itemRows}
    </tbody>
</table>

</body>
</html>`;
}

// ── Component ─────────────────────────────────────────────────────────────────

const PrintWindow: React.FC<PrintWindowProps> = ({ session, isBmw, filteredItems }) => {

    const handlePrint = () => {
        const iframe = document.getElementById('ifmcontentstoprint') as HTMLIFrameElement | null;
        if (!iframe?.contentWindow) return;

        const pri = iframe.contentWindow;
        pri.document.open();
        pri.document.write(buildPrintHTML(session, isBmw, filteredItems));
        pri.document.close();
        pri.focus();
        pri.print();
    };

    return (
        <>
            <button
                onClick={handlePrint}
                className="bg-gray-600 hover:bg-gray-700 text-white text-xs font-semibold py-1.5 px-3 rounded-lg transition"
            >
                <i className="fas fa-print mr-1"></i> Print
            </button>
            <iframe id="ifmcontentstoprint" style={{ height: 0, width: 0, position: 'absolute' }} title="print" />
        </>
    );
};

export default PrintWindow;
